import React, { useState, useContext, useEffect } from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { getCountries } from "react-phone-number-input/input"
import Flags from "react-phone-number-input/flags"
import en from "react-phone-number-input/locale/en.json"
import Downshift from "downshift"
import { LocaleContext } from "@src/context/locale-context"
import { useFormContext } from "react-hook-form"
import { Label } from "./input.styled"

const InputPlaceholder = styled.div`
  display: none;
`

const InputWrapper = styled.div`
  width: 100%;
`

const DropdownSelectArrow = css`
  position: absolute;
  display: block;
  content: "";
  width: 0;
  height: 0;

  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid ${props => props.theme.lightGray};
`

const DropdownIcon = styled.i`
  ${DropdownSelectArrow}
  right: 18px;
  bottom: 18px;
`

const StyledDropdown = styled.div`
  width: 100%;
  position: relative;
  ul {
    ${props => !props.isOpen && "border: none;"}
  }
`

const CountryInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  color: rgb(113, 113, 113);
  width: max-content;
  height: 100%;
  svg {
    height: 22px;
  }
`

const DropdownInput = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  max-height: 42px;
  border: 1px solid rgb(113, 113, 113);
  background: white;
  border-radius: 4px;
  padding: 10px 30px 10px 10px;
`

const StyledMenu = styled.ul`
  position: absolute;
  background-color: white;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid ${props => props.theme.borderGray};
  border-radius: 4px;
  z-index: 1000;
`

const StyledItem = styled.li`
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  background-color: ${props =>
    props.highlighted ? "rgba(254, 197, 27, 0.11)" : "white"};

  & .country-flag {
    margin-right: 10px;
    width: 32px;
  }

  &.pinned-country {
    font-weight: bold;
  }
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${props => props.theme.borderGray};
`

const countryList = getCountries()
const defaultPinnedCountries = ["CA", "US", "GB"]

const InputCountry = ({
  pinnedCountries: propPinnedCountries,
  placeholder,
  label,
  required,
  name,
  validate,
  ...props
}) => {
  const [countryOptions, setCountryOptions] = useState([])
  const { country: localeCountry } = useContext(LocaleContext)
  const pinnedCountries = propPinnedCountries || defaultPinnedCountries
  const [defaultCountry, setDefaultCountry] = useState(null)

  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext()

  useEffect(() => {
    if (localeCountry) {
      const initialCountry = {
        label: `${en[localeCountry]}`,
        value: localeCountry,
        icon: React.createElement(Flags[localeCountry], {
          title: en[localeCountry],
          className: "country-flag",
        }),
      }
      setDefaultCountry(initialCountry)
      setValue(name, initialCountry)
    }
  }, [localeCountry, name, setValue])

  useEffect(() => {
    setCountryOptions(
      countryList
        .map(country => {
          const countryOption = {
            label: `${en[country]}`,
            value: country,
            icon: React.createElement(Flags[country], {
              title: en[country],
              className: "country-flag",
            }),
          }
          if (pinnedCountries.includes(country)) {
            countryOption.className = "pinned-country"
          }
          return countryOption
        })
        .sort((a, b) => {
          if (pinnedCountries) {
            if (
              pinnedCountries.includes(a.value) &&
              !pinnedCountries.includes(b.value)
            ) {
              return -1
            } else if (
              pinnedCountries.includes(b.value) &&
              !pinnedCountries.includes(a.value)
            ) {
              return 1
            }
          }
          return `${en[a.value]}`.localeCompare(`${en[b.value]}`)
        }),
    )
  }, [pinnedCountries])

  const handleCountryChange = selectedItem => {
    setValue(name, selectedItem)
  }

  return (
    <InputWrapper>
      {label && (
        <Label htmlFor={name} required={required}>
          {label}
        </Label>
      )}
      {defaultCountry && (
        <Downshift
          onChange={handleCountryChange}
          itemToString={item => (item ? item.label : "")}
          initialSelectedItem={defaultCountry}
        >
          {downshiftProps => {
            const {
              getInputProps,
              getItemProps,
              getMenuProps,
              getRootProps,
              isOpen,
              highlightedIndex,
              selectedItem,
              getToggleButtonProps,
            } = downshiftProps

            return (
              <StyledDropdown
                {...getRootProps({ refKey: "ref" })}
                $isOpen={isOpen}
              >
                <InputPlaceholder
                  {...getInputProps({
                    ...register(name, { validate }),
                    placeholder: placeholder,
                    ...props,
                  })}
                ></InputPlaceholder>
                <DropdownInput {...getToggleButtonProps()}>
                  {selectedItem ? (
                    <CountryInfo>
                      {selectedItem.icon}
                      {selectedItem.label}
                    </CountryInfo>
                  ) : (
                    <CountryInfo>{placeholder}</CountryInfo>
                  )}

                  <DropdownIcon />
                </DropdownInput>

                <StyledMenu {...getMenuProps()}>
                  {isOpen &&
                    countryOptions.map((item, index) => (
                      <React.Fragment key={item.value}>
                        <StyledItem
                          {...getItemProps({
                            index,
                            item,
                            highlighted: highlightedIndex === index,
                            selected:
                              selectedItem && selectedItem.value === item.value,
                          })}
                          highlighted={selectedItem?.value == item?.value}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </StyledItem>
                        {index === pinnedCountries.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                </StyledMenu>
                {errors[name] && (
                  <p className="form-error">{errors[name].message}</p>
                )}
              </StyledDropdown>
            )
          }}
        </Downshift>
      )}
    </InputWrapper>
  )
}

InputCountry.propTypes = {
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  pinnedCountries: PropTypes.arrayOf(PropTypes.string),
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  validate: PropTypes.func,
}

export default InputCountry
